import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

import type { ChatDashboardItem, CanvasActions } from '@factorialco/f0-react'

import { toF0ChatDashboardConfig } from 'modules/ai/copilotActions/canvas/dashboards/f0Adapters'

import { savedDashboardConfigStore, savedDashboardMetaStore } from './configStore'
import type {
  DashboardCanvasContent,
  DashboardPersistedConfig,
  DashboardRuntimeConfig,
} from './types'
import { useSaveDashboardConfig } from './useSaveDashboardConfig'

// Mirror of f0's internal DashboardItemLayout (not part of the public types).
export type DashboardItemLayout = {
  id: string
  colSpan: number
  rowSpan: number
  itemHeight: number
  x: number
  y: number
}

export type PendingContext = {
  label: string
  context: string
}

type DashboardCanvasContextValue = {
  /** Whether there are unsaved layout changes */
  isDirty: boolean
  /** Incrementing counter — changes when discard is called to reset the grid */
  discardKey: number
  /** Pending item transforms keyed by item id (chart type changes, etc.) */
  itemTransforms: Map<string, Partial<ChatDashboardItem>>
  /** Called by the grid when layout changes (drag/resize/delete) */
  onLayoutChange: (layout: DashboardItemLayout[]) => void
  /** Persist current layout and update canvas */
  handleSave: () => Promise<void>
  /** Discard pending layout changes */
  handleDiscard: () => void
  /** Clear pending edits after they are persisted. */
  clearPendingChanges: () => void
  /** Transform an item's config (e.g. change chart type). Marks dirty without refetch. */
  transformItem: (itemId: string, patch: Partial<ChatDashboardItem>) => void
  /** Persist dashboard config plus saved-dashboard metadata to chat history. */
  saveConfigToHistory: (config: DashboardPersistedConfig) => Promise<void>
  /** Stage a snapshot date edit from the dashboard navigator pill. */
  setPendingSnapshotDate: (isoDate: string | null) => void
  /** Build the config that would be persisted by Save, including staged edits. */
  buildPendingConfig: () => DashboardRuntimeConfig | null
  /** Export the dashboard as Excel */
  exportAsExcel?: () => Promise<void>
  /** Register the export function from the dashboard component */
  registerExport: (fn: (() => Promise<void>) | undefined) => void
  /** Optional save/create callbacks forwarded from the host (`useAiChat`). */
  canvasActions?: CanvasActions
  /** Replace the current canvas content with a new one (e.g. after save-as). */
  onCanvasContentChange: (content: DashboardCanvasContent) => void
}

const DashboardCanvasContext = createContext<DashboardCanvasContextValue | null>(null)

export function useDashboardCanvas(): DashboardCanvasContextValue {
  const ctx = useContext(DashboardCanvasContext)
  if (!ctx) {
    throw new Error('useDashboardCanvas must be used within DashboardProvider')
  }
  return ctx
}

export type DashboardProviderProps = {
  content: DashboardCanvasContent
  children: ReactNode
  /** Save/create callbacks. Optional — when absent, save buttons render as no-ops. */
  canvasActions?: CanvasActions
  /** Replace the current canvas content with a new one (e.g. after save). */
  onCanvasContentChange: (content: DashboardCanvasContent) => void
  /** Current pending context — when present, save updates its serialised config. */
  pendingContext?: PendingContext | null
  setPendingContext?: (ctx: PendingContext | null) => void
  /** Current CopilotKit thread id. Used to gate history persistence. */
  threadId?: string
}

export function DashboardProvider({
  content,
  children,
  canvasActions,
  onCanvasContentChange,
  pendingContext,
  setPendingContext,
  threadId,
}: DashboardProviderProps): ReactNode {
  const [pendingLayout, setPendingLayout] = useState<DashboardItemLayout[] | null>(null)
  const [pendingSnapshotDate, setPendingSnapshotDate] = useState<string | null>(null)
  const [itemTransforms, setItemTransforms] = useState<Map<string, Partial<ChatDashboardItem>>>(
    new Map()
  )
  const [discardKey, setDiscardKey] = useState(0)
  const [exportAsExcel, setExportAsExcel] = useState<(() => Promise<void>) | undefined>()
  const registerExport = useCallback((fn: (() => Promise<void>) | undefined) => {
    setExportAsExcel(() => fn)
  }, [])

  const saveConfigFn = useSaveDashboardConfig(content.apiConfig)

  const applyLayout = useCallback(
    (
      config: DashboardRuntimeConfig,
      layout: DashboardItemLayout[]
    ): DashboardRuntimeConfig | null => {
      const itemsById = new Map(config.items.map((item) => [item.id, item]))
      const newItems = layout
        .map((entry) => {
          const original = itemsById.get(entry.id)
          if (!original) return null
          return {
            ...original,
            colSpan: entry.colSpan,
            // Persist canonical pixel-accurate height from the resize.
            // `rowSpan` is stripped on persistence so subsequent renders
            // read `itemHeight` exclusively (avoids drift).
            rowSpan: undefined,
            itemHeight: entry.itemHeight,
            x: entry.x,
            y: entry.y,
          }
        })
        .filter((item) => item !== null)

      return { ...config, items: newItems }
    },
    []
  )

  const onLayoutChange = useCallback((layout: DashboardItemLayout[]) => {
    setPendingLayout(layout)
  }, [])

  const transformItem = useCallback((itemId: string, patch: Partial<ChatDashboardItem>) => {
    setItemTransforms((prev) => {
      const next = new Map(prev)
      next.set(itemId, patch)
      return next
    })
  }, [])

  /** Apply item transforms to a config, producing a new config with patched items. */
  const applyTransforms = useCallback(
    (config: DashboardRuntimeConfig): DashboardRuntimeConfig => {
      if (itemTransforms.size === 0) return config
      return {
        ...config,
        items: config.items.map((item) => {
          const patch = itemTransforms.get(item.id)
          return patch ? Object.assign({}, item, patch) : item
        }),
      }
    },
    [itemTransforms]
  )

  const buildPendingConfig = useCallback((): DashboardRuntimeConfig | null => {
    let updatedConfig: DashboardRuntimeConfig | null = pendingLayout
      ? applyLayout(content.config, pendingLayout)
      : { ...content.config }
    if (!updatedConfig) return null

    updatedConfig = applyTransforms(updatedConfig)

    if (pendingSnapshotDate !== null) {
      updatedConfig = { ...updatedConfig, snapshotDate: pendingSnapshotDate }
    }

    return updatedConfig
  }, [applyLayout, applyTransforms, content.config, pendingLayout, pendingSnapshotDate])

  const stagePendingSnapshotDate = useCallback(
    (isoDate: string | null) => {
      setPendingSnapshotDate(isoDate && isoDate !== content.config.snapshotDate ? isoDate : null)
    },
    [content.config.snapshotDate]
  )

  const clearPendingChanges = useCallback(() => {
    setPendingLayout(null)
    setItemTransforms(new Map())
    setPendingSnapshotDate(null)
  }, [])

  const handleSave = async () => {
    const hasPendingChanges =
      pendingLayout ||
      itemTransforms.size > 0 ||
      pendingSnapshotDate !== null ||
      content.savedDashboardUnsaved
    if (!hasPendingChanges) return

    const updatedConfig = buildPendingConfig()
    if (!updatedConfig) return

    try {
      // Persist to chat history (best-effort).
      if (threadId) {
        try {
          const configWithMeta = {
            ...updatedConfig,
            ...(content.savedDashboardId
              ? {
                  savedDashboardId: content.savedDashboardId,
                  savedDashboardCategory: content.savedDashboardCategory,
                  savedDashboardDescription: content.savedDashboardDescription,
                  savedDashboardUnsaved: false,
                }
              : {}),
          }
          await saveConfigFn(threadId, content.toolCallId, configWithMeta)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[Dashboard] saveConfigToHistory failed, continuing with external save', err)
        }
      }

      // External persistence via canvasActions.
      if (
        content.savedDashboardId &&
        content.savedDashboardCategory &&
        canvasActions?.dashboard?.save
      ) {
        await canvasActions.dashboard.save(
          content.savedDashboardId,
          content.savedDashboardCategory,
          toF0ChatDashboardConfig(updatedConfig)
        )
      }

      if (content.toolCallId) {
        savedDashboardConfigStore.set(content.toolCallId, updatedConfig)
        savedDashboardMetaStore.set(content.toolCallId, {
          savedDashboardId: content.savedDashboardId,
          savedDashboardCategory: content.savedDashboardCategory,
          savedDashboardDescription: content.savedDashboardDescription,
          savedDashboardUnsaved: false,
        })
      }

      // Update pending context so the agent sees the latest config.
      if (pendingContext && setPendingContext) {
        setPendingContext({
          ...pendingContext,
          context: JSON.stringify({
            ...updatedConfig,
            savedDashboardId: content.savedDashboardId,
            savedDashboardCategory: content.savedDashboardCategory,
            savedDashboardDescription: content.savedDashboardDescription,
          }),
        })
      }

      onCanvasContentChange({
        ...content,
        config: updatedConfig,
        savedDashboardUnsaved: false,
      })

      clearPendingChanges()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Dashboard] save failed', err)
    }
  }

  const saveConfigToHistory = useCallback(
    async (config: DashboardPersistedConfig) => {
      if (!threadId) return
      try {
        await saveConfigFn(threadId, content.toolCallId, config)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Dashboard] saveConfigToHistory failed', err)
      }
    },
    [threadId, content.toolCallId, saveConfigFn]
  )

  const handleDiscard = () => {
    clearPendingChanges()
    setDiscardKey((k) => k + 1)
  }

  return (
    <DashboardCanvasContext.Provider
      value={{
        isDirty: pendingLayout !== null || itemTransforms.size > 0 || pendingSnapshotDate !== null,
        discardKey,
        itemTransforms,
        onLayoutChange,
        handleSave,
        handleDiscard,
        clearPendingChanges,
        transformItem,
        saveConfigToHistory,
        setPendingSnapshotDate: stagePendingSnapshotDate,
        buildPendingConfig,
        exportAsExcel,
        registerExport,
        canvasActions,
        onCanvasContentChange,
      }}
    >
      {children}
    </DashboardCanvasContext.Provider>
  )
}
